import { create } from 'zustand';

export interface TypingEntry {
  userId: string;
  expiresAt: number;
}

/**
 * The snapshot a selector returns for a chat nobody is typing in.
 *
 * Zustand compares snapshots by identity, so a selector that builds a fresh
 * `[]` each call never looks equal to the previous one and re-renders forever.
 * One shared frozen array keeps that comparison honest.
 */
export const NO_TYPING: readonly TypingEntry[] = Object.freeze([]);

interface PresenceState {
  onlineUserIds: Record<string, boolean>;
  /** Who is typing in each chat, with the moment the claim goes stale. */
  typingByChat: Record<string, TypingEntry[]>;

  setOnline: (userId: string, online: boolean) => void;
  setTyping: (chatId: string, userId: string, typing: boolean, ttlSeconds: number) => void;
  typingUserIds: (chatId: string) => string[];
}

/**
 * Live presence and typing signals.
 *
 * Both are deliberately kept out of the query cache: they are not facts the
 * server will repeat on request, they expire on their own, and they change far
 * too often to be worth invalidating queries over.
 *
 * Typing claims carry their own expiry because the "stopped typing" signal is
 * the one most likely to be lost — the sender closes the tab, loses signal, or
 * simply walks away — and an indicator that never goes out is worse than one
 * that appears a moment late.
 */
export const usePresenceStore = create<PresenceState>((set, get) => ({
  onlineUserIds: {},
  typingByChat: {},

  setOnline: (userId, online) =>
    set((state) => ({ onlineUserIds: { ...state.onlineUserIds, [userId]: online } })),

  setTyping: (chatId, userId, typing, ttlSeconds) =>
    set((state) => {
      const current = (state.typingByChat[chatId] ?? []).filter(
        (entry) => entry.userId !== userId,
      );
      const next = typing
        ? [...current, { userId, expiresAt: Date.now() + ttlSeconds * 1000 }]
        : current;
      return { typingByChat: { ...state.typingByChat, [chatId]: next } };
    }),

  typingUserIds: (chatId) => {
    const now = Date.now();
    return (get().typingByChat[chatId] ?? [])
      .filter((entry) => entry.expiresAt > now)
      .map((entry) => entry.userId);
  },
}));
